import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowDown, ArrowUp, Plus, ChevronRight, GitCommit, MessageSquarePlus, Pencil, Trash2, Archive, ArchiveRestore } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { format } from 'date-fns';

const STAGES = [
  { id: 'purpose', label: 'Purpose', color: 'text-purple-400', border: 'border-purple-500/50', bg: 'bg-purple-500/10' },
  { id: 'direction', label: 'Direction', color: 'text-indigo-400', border: 'border-indigo-500/50', bg: 'bg-indigo-500/10' },
  { id: 'strategy', label: 'Strategy', color: 'text-blue-400', border: 'border-blue-500/50', bg: 'bg-blue-500/10' },
  { id: 'tactics', label: 'Tactics', color: 'text-cyan-400', border: 'border-cyan-500/50', bg: 'bg-cyan-500/10' },
  { id: 'operations', label: 'Operations', color: 'text-teal-400', border: 'border-teal-500/50', bg: 'bg-teal-500/10' },
  { id: 'execution', label: 'Execution', color: 'text-emerald-400', border: 'border-emerald-500/50', bg: 'bg-emerald-500/10' },
];

export default function Framework() {
  const queryClient = useQueryClient();
  const { data: nodes, isLoading } = useQuery({
    queryKey: ['strategicNodes'],
    queryFn: () => base44.entities.StrategicNode.list(),
    initialData: []
  });

  const createNodeMutation = useMutation({
    mutationFn: (data) => base44.entities.StrategicNode.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['strategicNodes'] })
  });

  const updateNodeMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.StrategicNode.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['strategicNodes'] })
  });

  const deleteNodeMutation = useMutation({
    mutationFn: (id) => base44.entities.StrategicNode.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['strategicNodes'] })
  });

  const updateFeedbackMutation = useMutation({
    mutationFn: ({ id, feedback }) => base44.entities.StrategicNode.update(id, { feedback_notes: feedback }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['strategicNodes'] })
  });

  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [activeStage, setActiveStage] = React.useState(null);
  const [newNode, setNewNode] = React.useState({ title: '', description: '' });
  const [editingNode, setEditingNode] = React.useState(null);
  const [showArchived, setShowArchived] = React.useState(false);
  
  // Feedback Dialog State
  const [feedbackNode, setFeedbackNode] = React.useState(null);
  const [feedbackText, setFeedbackText] = React.useState('');
  const [expandedNodeId, setExpandedNodeId] = React.useState(null);

  const handleUpdateFeedback = (e) => {
    e.preventDefault();
    if (feedbackNode) {
      updateFeedbackMutation.mutate({ id: feedbackNode.id, feedback: feedbackText });
      setFeedbackNode(null);
      setFeedbackText('');
    }
  };

  const handleCreateOrUpdate = (e) => {
    e.preventDefault();
    if (editingNode) {
      updateNodeMutation.mutate({
        id: editingNode.id,
        data: { ...newNode }
      });
    } else {
      createNodeMutation.mutate({
        ...newNode,
        type: activeStage,
        status: 'active',
        order: getNodesByType(activeStage).length
      });
    }
    setIsDialogOpen(false);
    setNewNode({ title: '', description: '' });
    setEditingNode(null);
  };

  const handleEdit = (node) => {
    setEditingNode(node);
    setNewNode({ title: node.title, description: node.description || '' });
    setActiveStage(node.type);
    setIsDialogOpen(true);
  };

  const getNodesByType = (type) => nodes
    .filter(n => n.type === type)
    .filter(n => showArchived ? n.status === 'archived' : n.status !== 'archived')
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const archiveNodeMutation = useMutation({
    mutationFn: ({ id, isArchived }) => {
      if (isArchived) {
        return base44.entities.StrategicNode.update(id, { status: 'active', archived_date: null });
      } else {
        return base44.entities.StrategicNode.update(id, { status: 'archived', archived_date: new Date().toISOString() });
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['strategicNodes'] })
  });

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const sourceStageId = source.droppableId;
    const destStageId = destination.droppableId;
    
    if (sourceStageId === destStageId) {
      const items = Array.from(getNodesByType(sourceStageId));
      const [reorderedItem] = items.splice(source.index, 1);
      items.splice(destination.index, 0, reorderedItem);

      items.forEach((node, index) => {
        if (node.order !== index) {
          updateNodeMutation.mutate({ id: node.id, data: { order: index } });
        }
      });
    } else {
      const sourceItems = Array.from(getNodesByType(sourceStageId));
      const [movedItem] = sourceItems.splice(source.index, 1);
      
      const destItems = Array.from(getNodesByType(destStageId));
      destItems.splice(destination.index, 0, movedItem);

      // Update moved item type and order
      updateNodeMutation.mutate({ 
        id: movedItem.id, 
        data: { type: destStageId, order: destination.index } 
      });

      // Update orders in dest stage
      destItems.forEach((node, index) => {
        if (node.id === movedItem.id) return; // Already updated above
        if (node.order !== index) {
          updateNodeMutation.mutate({ id: node.id, data: { order: index } });
        }
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="mb-8 flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">The Framework</h1>
          <p className="text-slate-400 max-w-2xl">
            Flow from Purpose to Execution. Top items define scope and constraints for items below.
            Bottom items provide feedback upwards.
          </p>
        </div>
        <div className="flex gap-4 items-center">
          <Button
            variant={showArchived ? "default" : "outline"}
            size="sm"
            onClick={() => setShowArchived(!showArchived)}
            className={showArchived ? "bg-slate-700" : "border-slate-700 text-slate-400"}
          >
            <Archive className="w-4 h-4 mr-2" />
            {showArchived ? 'Show Active' : 'Show Archived'}
          </Button>
          <div className="flex gap-4 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <ArrowDown className="w-4 h-4 text-slate-400" /> Scope Flow
            </div>
            <div className="flex items-center gap-2">
              <ArrowUp className="w-4 h-4 text-slate-400" /> Feedback Loop
            </div>
          </div>
        </div>
      </div>

      <div className="pb-20 w-full">
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex flex-col gap-8">
            {STAGES.map((stage, index) => (
              <div key={stage.id} className="relative group pl-8 md:pl-0">
                {/* Connector Lines */}
                {index < STAGES.length - 1 && (
                  <div className="absolute left-[2.25rem] md:left-[8.5rem] -bottom-8 h-8 border-l-2 border-dashed border-slate-700 z-0" />
                )}
                
                <div className="flex flex-col gap-4 items-start w-full">
                  <div className={`
                    w-full p-5 rounded-lg border backdrop-blur-sm
                    ${stage.border} ${stage.bg}
                  `}>
                    <h3 className={`font-mono font-bold uppercase tracking-wider text-base ${stage.color}`}>
                      {stage.label}
                    </h3>
                  </div>

                  <Droppable droppableId={stage.id} direction="horizontal" type="NODE">
                    {(provided) => (
                      <div 
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
                      >
                        {getNodesByType(stage.id).map((node, index) => (
                          <Draggable key={node.id} draggableId={node.id} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="relative"
                              >
                                <Card 
                                  className="bg-slate-900/80 border-slate-800 hover:border-slate-600 transition-colors cursor-pointer group/card h-full"
                                  onClick={() => setExpandedNodeId(expandedNodeId === node.id ? null : node.id)}
                                >
                                  <CardContent className="p-5 space-y-3">
                                    <div className="flex justify-between items-start gap-2">
                                      <h4 className="text-slate-200 font-medium text-base leading-tight pr-6">
                                        {node.title}
                                      </h4>
                                      <div className="absolute top-2 right-2 opacity-0 group-hover/card:opacity-100 transition-opacity flex gap-1 bg-slate-900/80 rounded p-1">
                                        <Button 
                                          size="icon" 
                                          variant="ghost" 
                                          className="h-6 w-6 text-slate-400 hover:text-white"
                                          onClick={(e) => { e.stopPropagation(); handleEdit(node); }}
                                        >
                                          <Pencil className="w-3 h-3" />
                                        </Button>
                                        <Button 
                                          size="icon" 
                                          variant="ghost" 
                                          className="h-6 w-6 text-slate-400 hover:text-amber-400"
                                          onClick={(e) => { 
                                            e.stopPropagation(); 
                                            archiveNodeMutation.mutate({ id: node.id, isArchived: node.status === 'archived' }); 
                                          }}
                                        >
                                          {node.status === 'archived' ? <ArchiveRestore className="w-3 h-3" /> : <Archive className="w-3 h-3" />}
                                        </Button>
                                        <Button 
                                          size="icon" 
                                          variant="ghost" 
                                          className="h-6 w-6 text-slate-400 hover:text-red-400"
                                          onClick={(e) => { e.stopPropagation(); deleteNodeMutation.mutate(node.id); }}
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                    {node.description && (
                                      <p className={`text-sm text-slate-500 whitespace-pre-wrap ${expandedNodeId === node.id ? '' : 'line-clamp-3'}`}>{node.description}</p>
                                    )}
                                    {(node.feedback_notes || activeStage) && (
                                      <div 
                                        className="mt-2 pt-2 border-t border-slate-800/50 flex items-start gap-1.5 group/feedback cursor-pointer hover:bg-slate-800/50 rounded -mx-2 px-2 py-1 transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setFeedbackNode(node);
                                          setFeedbackText(node.feedback_notes || '');
                                        }}
                                      >
                                        <GitCommit className={`w-3 h-3 mt-0.5 ${node.feedback_notes ? 'text-emerald-500' : 'text-slate-600'}`} />
                                        <div className="flex-1">
                                          {node.feedback_notes ? (
                                            <p className="text-[10px] text-emerald-500/80 leading-tight">
                                              {node.feedback_notes}
                                            </p>
                                          ) : (
                                            <p className="text-[10px] text-slate-600 italic leading-tight group-hover/feedback:text-slate-400">
                                              Add feedback loop...
                                            </p>
                                          )}
                                        </div>
                                        <MessageSquarePlus className="w-3 h-3 text-slate-600 opacity-0 group-hover/feedback:opacity-100 transition-opacity" />
                                      </div>
                                    )}
                                    <div className="mt-2 pt-2 border-t border-slate-800/50 flex justify-end">
                                      <p className="text-[10px] text-cyan-400/60">
                                        {format(new Date(node.created_date), 'MMM d, yyyy')}
                                        {node.archived_date && ` - ${format(new Date(node.archived_date), 'MMM d, yyyy')}`}
                                        {!node.archived_date && node.status !== 'archived' && ' - '}
                                      </p>
                                    </div>
                                  </CardContent>
                                  
                                  {/* Connection Dot */}
                                  <div className="absolute left-1/2 -bottom-1.5 w-3 h-3 bg-slate-950 border border-slate-700 rounded-full z-10 opacity-0 group-hover/card:opacity-100 transition-opacity -translate-x-1/2" />
                                  <div className="absolute left-1/2 -top-1.5 w-3 h-3 bg-slate-950 border border-slate-700 rounded-full z-10 opacity-0 group-hover/card:opacity-100 transition-opacity -translate-x-1/2" />
                                </Card>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        
                        {/* Add Button as a "fake" grid item? No, keep it separate or inside */}
                        <Dialog open={isDialogOpen && activeStage === stage.id} onOpenChange={(open) => {
                          setIsDialogOpen(open);
                          if (open) {
                             setActiveStage(stage.id);
                             setNewNode({ title: '', description: '' });
                             setEditingNode(null);
                          } else {
                             setEditingNode(null);
                          }
                        }}>
                          <DialogTrigger asChild>
                            <button className="h-full min-h-[120px] py-4 border border-dashed border-slate-800 rounded-lg text-slate-600 hover:text-slate-400 hover:border-slate-600 transition-colors flex flex-col items-center justify-center gap-2 text-sm group/btn">
                              <div className="p-2 rounded-full bg-slate-900 group-hover:bg-slate-800 transition-colors">
                                <Plus className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                              </div>
                              Add Node
                            </button>
                          </DialogTrigger>
                          <DialogContent data-testid="fw-framework-dialog" className="bg-slate-900 border-slate-800 text-slate-100">
                            <DialogHeader>
                              <DialogTitle>{editingNode ? `Edit ${stage.label}` : `Define ${stage.label}`}</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleCreateOrUpdate} className="space-y-4 mt-4">
                              <div className="space-y-2">
                                <Label>Title</Label>
                                <Input data-testid="fw-framework-input" 
                                  value={newNode.title} 
                                  onChange={e => setNewNode({...newNode, title: e.target.value})}
                                  placeholder={`Enter ${stage.label} title...`}
                                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Scope & Constraints (Description)</Label>
                                <Textarea data-testid="fw-framework-textarea" 
                                  value={newNode.description}
                                  onChange={e => setNewNode({...newNode, description: e.target.value})}
                                  placeholder="Define the boundaries and scope..."
                                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                                />
                              </div>
                              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                                {editingNode ? 'Update Node' : 'Create Node'}
                              </Button>
                            </form>
                          </DialogContent>
                          </Dialog>
                          </div>
                    )}
                  </Droppable>
                </div>
              </div>
            ))}
          </div>
        </DragDropContext>

        {/* Feedback Dialog */}
        <Dialog open={!!feedbackNode} onOpenChange={(open) => !open && setFeedbackNode(null)}>
          <DialogContent data-testid="fw-framework-dialog-1" className="bg-slate-900 border-slate-800 text-slate-100">
            <DialogHeader>
              <DialogTitle>Provide Feedback</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateFeedback} className="space-y-4 mt-4">
              <p className="text-sm text-slate-400">
                Adding feedback to: <span className="text-white font-medium">{feedbackNode?.title}</span>
              </p>
              <div className="space-y-2">
                <Label>Feedback Notes (Right-to-Left Signal)</Label>
                <Textarea data-testid="fw-framework-textarea-1" 
                  value={feedbackText}
                  onChange={e => setFeedbackText(e.target.value)}
                  placeholder="What data or insight is feeding back to this stage?"
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 min-h-[100px]"
                />
              </div>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">
                Log Feedback Signal
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}